import { Scene } from 'phaser';

export class WaveSystem {
  private scene: Scene;
  private currentWave: number = 1;
  private enemiesPerWave: number = 5;
  private waveDuration: number = 30000; // 30 seconds per wave
  private waveTimer: Phaser.Time.TimerEvent;
  private enemiesSpawned: number = 0;
  private enemiesDestroyed: number = 0;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  public start(): void {
    this.startWave();
  }

  public stop(): void {
    if (this.waveTimer) {
      this.waveTimer.remove();
    }
  }

  private startWave(): void {
    this.enemiesSpawned = 0;
    this.enemiesDestroyed = 0;
    
    this.scene.events.emit('wave-started', this.currentWave);
    
    // Start wave timer
    this.waveTimer = this.scene.time.addEvent({
      delay: this.waveDuration,
      callback: () => this.endWave(),
      callbackScope: this
    });
  }

  private endWave(): void {
    this.scene.events.emit('wave-ended', {
      wave: this.currentWave,
      enemiesSpawned: this.enemiesSpawned,
      enemiesDestroyed: this.enemiesDestroyed
    });
    
    this.currentWave++;
    this.increaseDifficulty();
    
    // Start next wave after a short break
    this.scene.time.delayedCall(3000, () => {
      this.startWave();
    });
  }

  private increaseDifficulty(): void {
    // Increase enemies per wave
    this.enemiesPerWave = Math.min(20, this.enemiesPerWave + 1);
    
    // Decrease wave duration (minimum 15 seconds)
    this.waveDuration = Math.max(15000, this.waveDuration - 500);
    
    this.scene.events.emit('difficulty-increased', {
      wave: this.currentWave,
      enemiesPerWave: this.enemiesPerWave,
      waveDuration: this.waveDuration
    });
  }

  public onEnemySpawned(): void {
    this.enemiesSpawned++;
    this.scene.events.emit('enemy-spawned-count', this.enemiesSpawned);
  }

  public onEnemyDestroyed(): void {
    this.enemiesDestroyed++;
    this.scene.events.emit('enemy-destroyed-count', this.enemiesDestroyed);
    
    // Check if wave should end early (all enemies destroyed)
    if (this.enemiesDestroyed >= this.enemiesPerWave) {
      this.endWave();
    }
  }

  public getCurrentWave(): number {
    return this.currentWave;
  }

  public getEnemiesPerWave(): number {
    return this.enemiesPerWave;
  }

  public getWaveDuration(): number {
    return this.waveDuration;
  }

  public getWaveProgress(): number {
    return this.enemiesDestroyed / this.enemiesPerWave;
  }

  public getTimeRemaining(): number {
    if (!this.waveTimer) {
      return 0;
    }
    
    return this.waveTimer.getRemainingSeconds();
  }

  public getWaveStats(): {
    currentWave: number;
    enemiesSpawned: number;
    enemiesDestroyed: number;
    enemiesPerWave: number;
    timeRemaining: number;
  } {
    return {
      currentWave: this.currentWave,
      enemiesSpawned: this.enemiesSpawned,
      enemiesDestroyed: this.enemiesDestroyed,
      enemiesPerWave: this.enemiesPerWave,
      timeRemaining: this.getTimeRemaining()
    };
  }

  public reset(): void {
    this.currentWave = 1;
    this.enemiesPerWave = 5;
    this.waveDuration = 30000;
    this.enemiesSpawned = 0;
    this.enemiesDestroyed = 0;
    this.stop();
  }
}